import { InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { DiaryEntry } from "@/types/person"

interface FetchDiaryEntriesOptions {
  personId?: string // Person UUID
  limit?: number
  offset?: string
  hasFollowUpAction?: boolean // Filter by whether follow_up_action exists and is not empty
  actionCompleted?: boolean // Filter by action_completed status
  driverManagerTeam?: string // Filter by driver_manager_team
  engagementTag?: string // Filter by engagement_tag
  engagementSubTag?: string // Filter by engagement_sub_tag
  startDate?: string // Filter by call_date_time >= startDate (ISO string)
  endDate?: string // Filter by call_date_time <= endDate (ISO string)
  location?: string // Filter by location (checks if location array contains this value)
  sheriffArea?: string // Filter by sheriff_areas (checks if sheriff_areas array contains this value)
}

interface DiaryEntriesResponse {
  data: DiaryEntry[]
  offset?: string
}

/**
 * Standard query: For fetching diary entries for a specific person or filtered set.
 */
export const useFetchDiaryEntries = (options: FetchDiaryEntriesOptions = {}) => {
  return useQuery({
    queryKey: ["diaryEntries", options],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-diary-entries", {
        body: options,
      })

      if (error) {
        throw new Error(`Error fetching diary entries: ${error.message}`)
      }

      if (!data || !data.data) {
        return { data: [], offset: undefined }
      }

      // The Edge Function now returns fully mapped DiaryEntry objects
      const diaryResponse = data as DiaryEntriesResponse
      const diaryEntries = diaryResponse.data

      return {
        data: diaryEntries,
        offset: diaryResponse.offset,
      }
    },
    // Enabled if personId is provided or any filter is provided:
    enabled: Boolean(
      options.personId ||
      options.hasFollowUpAction !== undefined ||
      options.actionCompleted !== undefined ||
      options.driverManagerTeam ||
      options.engagementTag ||
      options.engagementSubTag ||
      options.startDate ||
      options.endDate ||
      options.location ||
      options.sheriffArea,
    ),
  })
}

/**
 * Infinite query: For fetching all/filtered diary entries for infinite/paginated scroll.
 * Use when you want to paginate through lots of entries (e.g. the diary entry list tabs).
 *
 * Stays idle until at least one filter is set, so callers that pass undefined filters
 * while a drawer is closed do not fetch. Pass fetchAll to browse without filters.
 *
 * Typings: data type is InfiniteData<DiaryEntriesResponse>,
 *   access with data?.pages[...].data
 */
export const useInfiniteDiaryEntries = ({
  fetchAll = false,
  ...options
}: Omit<FetchDiaryEntriesOptions, "offset"> & { fetchAll?: boolean } = {}) => {
  return useInfiniteQuery<
    DiaryEntriesResponse,
    Error,
    InfiniteData<DiaryEntriesResponse>,
    [string, Omit<FetchDiaryEntriesOptions, "offset">],
    string | undefined
  >({
    queryKey: ["diaryEntries-infinite", options],
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.offset,
    queryFn: async ({ pageParam = undefined }) => {
      const { data, error } = await supabase.functions.invoke("fetch-diary-entries", {
        body: { ...options, offset: pageParam },
      })
      if (error) {
        throw new Error(`Error fetching diary entries: ${error.message}`)
      }
      if (!data || !data.data) {
        return { data: [], offset: undefined }
      }
      const diaryResponse = data as DiaryEntriesResponse
      return {
        data: diaryResponse.data,
        offset: diaryResponse.offset,
      }
    },
    enabled:
      fetchAll ||
      Boolean(
        options.personId ||
        options.hasFollowUpAction !== undefined ||
        options.actionCompleted !== undefined ||
        options.driverManagerTeam ||
        options.engagementTag ||
        options.engagementSubTag,
      ),
  })
}
